import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import BoardColumn from "./BoardColumn";
import IssueCard from "./IssueCard";
import { useKanbanBoardLayout } from "./kanbanBoardLayout";
import { RiSearchLine, RiArrowDownSLine, RiUser3Line, RiCornerDownLeftLine } from "react-icons/ri";
import { useState, useMemo, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  createWorkItem,
  fetchWorkItemById,
  fetchWorkItemsByProjectId,
  fetchProjectsData,
  fetchAllWorkItemsForGlobalKanban,
} from "@/Redux/Thunks/projectManagementThunk";
import { fetchBacklogData } from "@/Redux/Thunks/backlogThunk";
import { mapWorkItemFromApiPayload } from "@/Pages/Projects/projectMappers";
import { resolveProjectIdForWorkItemFetch } from "@/Pages/Projects/project-detail/backlog/resolveProjectIdForWorkItemFetch";
import BacklogEditModal from "@/Pages/Projects/project-detail/backlog/BacklogEditModal";
import BacklogTaskForm from "@/Pages/Projects/project-detail/backlog/BacklogTaskForm";
import { getThunkRejectMessage } from "@/utils/thunkErrorMessage";
import toast from "react-hot-toast";
import { fetchUsersData } from "@/Redux/Thunks/usersThunk";
import { fetchClientsData } from "@/Redux/Thunks/clientsThunk";
import { fetchSystemList } from "@/Redux/Thunks/systemListsThunk";
import { selectUsersData } from "@/Redux/Slices/usersSlice";
import {
  selectProjectStatusesResponse,
  selectProjectStatusesLoading,
} from "@/Redux/Slices/projectStatusesSlice";
import { selectIssuesLoading } from "@/Redux/Slices/issuesSlice";
import { selectProjectsListForDropdown } from "@/Redux/Slices/projectsManagementSlice";
import { selectClientsData } from "@/Redux/Slices/clientsSlice";
import {
  getListFromApiResponse,
  mapUserToAssigneeOptionForKanban,
  mapSystemListToKanbanInlineOptions,
  KANBAN_INLINE_ISSUE_TYPES,
  buildProjectKanbanCategoryColumns,
  buildGlobalKanbanColumns,
  resolveProjectKanbanColumnKey,
  resolveProjectStatusIdForCategory,
  resolveGlobalKanbanColumnKey,
  formatWorkItemProjectClientLabel,
} from "@/Pages/Issues/issueMappers";
import { DISPLAY_DATE_FORMAT_PICKER_MOMENT } from "@/utils/dateUtils";
import SingleDatePicker from "@/Utility/DatePickerRange/SingleDatePicker";
import ProjectBacklog from "./ProjectBacklog";
import Input from "@/Utility/Input/Input";
import Typography from "@/Utility/Typography";
import Button from "@/Utility/Button/Button";
import AddButton from "@/Utility/AddButton/AddButton";
import CreateModal from "@/Pages/Issues/CreateModal/CreateModal";
import CreateTaskForm from "@/Pages/Issues/CreateModal/CreateTaskForm";
import Loader from "@/Utility/Loader/Loader";
import { usePermissions } from "@/hooks/usePermissions";
import { PERMISSION_KEYS } from "@/permissions/permissionKeys";
import PermissionDeniedModal from "@/Utility/PermissionDeniedModal/PermissionDeniedModal";
import GlobalKanbanQuickFilter from "@/Pages/Issues/GlobalKanbanQuickFilter";
import ToggleSwitch from "@/Utility/ToggleSwitch/ToggleSwitch";

function resolveTypeIdForLabel(typeOptions, label) {
  const wanted = String(label ?? "")
    .trim()
    .toLowerCase();
  if (!wanted) return null;
  const list = Array.isArray(typeOptions) ? typeOptions : [];
  const found = list.find(
    (o) =>
      String(o?.label ?? "")
        .trim()
        .toLowerCase() === wanted
  );
  return found?.value ?? found?.id ?? null;
}

function resolvePriorityIdForLabel(priorityOptions, label) {
  const wanted = String(label ?? "")
    .trim()
    .toLowerCase();
  if (!wanted) return null;
  const list = Array.isArray(priorityOptions) ? priorityOptions : [];
  const found = list.find(
    (o) =>
      String(o?.label ?? "")
        .trim()
        .toLowerCase() === wanted
  );
  return found?.value ?? found?.id ?? null;
}

/* ===================== UTILS ===================== */
const colorPalette = [
  "#2563EB",
  "#7C3AED",
  "#0F766E",
  "#EA580C",
  "#1E293B",
  "#DB2777",
  "#059669",
  "#9333EA",
];

const getColorByIndex = (index) => colorPalette[index % colorPalette.length];

/** Project label for cards / search — normalized rows use `projectName`; API may nest under `project`. */
function resolveKanbanIssueProjectLabel(issue) {
  return formatWorkItemProjectClientLabel(issue);
}

function issueMatchesKanbanSearch(issue, searchTerm) {
  const q = String(searchTerm ?? "")
    .trim()
    .toLowerCase();
  if (!q) return true;
  const pn = resolveKanbanIssueProjectLabel(issue).toLowerCase();
  return (
    (issue.issueKey && String(issue.issueKey).toLowerCase().includes(q)) ||
    (issue.title && String(issue.title).toLowerCase().includes(q)) ||
    (pn && pn.includes(q))
  );
}

/* ===================== MAIN ===================== */
const KanbanBoard = ({
  issues = [],
  onStatusChange,
  projectSlug,
  statusPatchPending = false,
  onOpenWorkItemDetail,
  /** When set with {@link onAssigneeFilterIdsChange}, assignee chips refetch list via parent (server `assigneeId`). */
  assigneeFilterIds,
  onAssigneeFilterIdsChange,
  /** Refetch all board items (after create / inline create / drag-drop). Works for both project and global boards. */
  onRefetchProjectKanbanIssues,
  /** Global Kanban: parent refetch using full saved filter + participant scope (modal create success). */
  onRefetchGlobalKanban: _onRefetchGlobalKanban,
  /** Project Kanban: true while list refetch is in flight (assignee filter, etc.) — pairs with Redux loading for reliable UI. */
  forceListLoading = false,
  /**
   * Per-column paginated state from kanbanColumnsSlice.
   * When set, the board renders from this data instead of grouping the flat `issues` list.
   * Shape: { [columnKey]: { items, hasMore, loadingMore, initialLoading, error } }
   */
  columnData,
  /** Called when user clicks "Load more / Load older" for a column. Receives `columnKey`. */
  onLoadMore,
  /** Global Kanban: column defs from GET /work-items/kanban/initial (Redux `boardColumnDefs`). */
  boardColumns: boardColumnsProp,
  showOnHold = false,
  onShowOnHoldChange,
  /**
   * When set with {@link onSearchTermChange}, the search box is server-driven: parent debounces
   * and refetches via the `search` query param, so `colState.items`/`colState.total` already
   * reflect the search. Uncontrolled fallback filters the currently-loaded items client-side.
   */
  searchTerm,
  onSearchTermChange,
}) => {
  const dispatch = useDispatch();
  const issuesLoading = useSelector(selectIssuesLoading);
  const statusesLoading = useSelector((state) =>
    projectSlug ? selectProjectStatusesLoading(state, projectSlug) : false
  );
  const { can } = usePermissions();
  const canViewKanbanBoard = can(PERMISSION_KEYS.KANBAN_VIEW);
  const canCreateWorkItem = can(PERMISSION_KEYS.WORK_ITEMS_CREATE);
  const canEditWorkItem = can(PERMISSION_KEYS.WORK_ITEMS_EDIT);
  const [deniedPermissionKey, setDeniedPermissionKey] = useState(null);
  const usersData = useSelector(selectUsersData);
  const projectsData = useSelector(selectProjectsListForDropdown);
  const clientsData = useSelector(selectClientsData);
  const isGlobalBoard = !projectSlug;
  const statusesResponse = useSelector((state) =>
    projectSlug ? selectProjectStatusesResponse(state, projectSlug) : null
  );
  const projectsList = getListFromApiResponse(projectsData);
  const clientsList = getListFromApiResponse(clientsData);
  const usersListRaw = getListFromApiResponse(usersData);

  const columns = useMemo(() => {
    if (boardColumnsProp?.length) return boardColumnsProp;
    if (isGlobalBoard) return buildGlobalKanbanColumns();
    return buildProjectKanbanCategoryColumns(statusesResponse);
  }, [boardColumnsProp, isGlobalBoard, statusesResponse]);

  const { gridTemplateColumns, boardStyle } = useKanbanBoardLayout(columns.length);
  const [dragPreviewWidth, setDragPreviewWidth] = useState(null);

  /** Clicks navigate cards; small movement still counts as click (same UX as list row). */
  const dragSensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    })
  );
  const [activeId, setActiveId] = useState(null);
  const [localSearchTerm, setLocalSearchTerm] = useState("");
  const isSearchControlled = typeof onSearchTermChange === "function";
  const effectiveSearchTerm = isSearchControlled ? (searchTerm ?? "") : localSearchTerm;
  const setEffectiveSearchTerm = isSearchControlled ? onSearchTermChange : setLocalSearchTerm;
  const [localAssigneeFilterIds, setLocalAssigneeFilterIds] = useState([]);
  const isAssigneeFilterControlled = typeof onAssigneeFilterIdsChange === "function";
  const selectedAssignees = isAssigneeFilterControlled
    ? (assigneeFilterIds ?? [])
    : localAssigneeFilterIds;
  const setSelectedAssignees = isAssigneeFilterControlled
    ? onAssigneeFilterIdsChange
    : setLocalAssigneeFilterIds;
  const [showAssigneePopup, setShowAssigneePopup] = useState(false);
  const assigneeFilterRef = useRef(null);
  const [createSubmitting, setCreateSubmitting] = useState(false);
  const [workTypeOptions, setWorkTypeOptions] = useState([]);
  const [priorityOptions, setPriorityOptions] = useState([]);

  const [createColumnKey, setCreateColumnKey] = useState(null);
  const [openCreateModal, setOpenCreateModal] = useState(false);
  const [openBacklogEditModal, setOpenBacklogEditModal] = useState(false);
  const [backlogEditSubmitting, setBacklogEditSubmitting] = useState(false);
  const [backlogEditContext, setBacklogEditContext] = useState(null);
  const [backlogEditPrefillLoading, setBacklogEditPrefillLoading] = useState(false);

  useEffect(() => {
    dispatch(fetchUsersData({ getAll: true }));
  }, [dispatch]);

  useEffect(() => {
    dispatch(
      fetchSystemList({
        listType: "WORK_ITEM_TYPE",
        context: "kanban-inline-create-type",
      })
    )
      .unwrap()
      .then((list) => setWorkTypeOptions(mapSystemListToKanbanInlineOptions(list)))
      .catch(() => setWorkTypeOptions([]));
  }, [dispatch]);

  useEffect(() => {
    dispatch(
      fetchSystemList({
        listType: "PRIORITY",
        context: "kanban-inline-create-priority",
      })
    )
      .unwrap()
      .then((list) => setPriorityOptions(mapSystemListToKanbanInlineOptions(list)))
      .catch(() => setPriorityOptions([]));
  }, [dispatch]);

  useEffect(() => {
    if (!projectsList?.length) dispatch(fetchProjectsData({ getAll: true }));
    if (!clientsList?.length) dispatch(fetchClientsData({ getAll: true }));
  }, [dispatch, projectsList?.length, clientsList?.length]);

  useEffect(() => {
    if (!showAssigneePopup) return;
    const handlePointerDown = (e) => {
      if (assigneeFilterRef.current && !assigneeFilterRef.current.contains(e.target)) {
        setShowAssigneePopup(false);
      }
    };
    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, [showAssigneePopup]);

  const assigneeOptions = useMemo(() => {
    const list = getListFromApiResponse(usersData);
    return list
      .map((user, index) => mapUserToAssigneeOptionForKanban(user, index, getColorByIndex))
      .filter((u) => u.userId != null && String(u.userId).trim() !== "");
  }, [usersData]);

  // When columnData is provided, per-column initialLoading handles the UX — suppress the full overlay.
  const showKanbanLoadingOverlay =
    !columnData &&
    ((!isGlobalBoard && Boolean(projectSlug) && forceListLoading) ||
      (isGlobalBoard && issuesLoading) ||
      (!isGlobalBoard && Boolean(projectSlug) && (issuesLoading || statusesLoading)));

  /* ===================== FILTER (assignee = server; search = server when controlled) ===================== */
  // When columnData is provided, each column filters its own items at render time.
  const filteredIssues = useMemo(() => {
    if (columnData) return []; // unused in lazy-loading mode
    return issues.filter((issue) => {
      return issueMatchesKanbanSearch(issue, effectiveSearchTerm);
    });
  }, [issues, effectiveSearchTerm, columnData]);

  const activeIssue = useMemo(() => {
    if (!activeId) return null;
    if (columnData) {
      // Flatten all column items to find the dragged card (issues=[] in lazy mode).
      return (
        Object.values(columnData)
          .flatMap((col) => col?.items ?? [])
          .find((i) => i.id === activeId) ?? null
      );
    }
    return issues.find((i) => i.id === activeId) ?? null;
  }, [activeId, columnData, issues]);

  const openEditModalFromKanbanBacklogRow = async (issue) => {
    const wid = issue?.id ?? issue?.workItemId;
    if (wid == null || String(wid).trim() === "") {
      toast.error("Cannot open this row: missing work item id.");
      return;
    }
    const resolvedProjectId = resolveProjectIdForWorkItemFetch(
      projectSlug ?? "",
      issue,
      projectsData
    );
    if (!resolvedProjectId || String(resolvedProjectId).trim() === "") {
      toast.error("Missing project context for this work item.");
      return;
    }
    const workItemId = String(wid).trim();
    setBacklogEditPrefillLoading(true);
    try {
      const raw = await dispatch(
        fetchWorkItemById({ projectId: resolvedProjectId, workItemId })
      ).unwrap();
      const normalizedWorkItem = mapWorkItemFromApiPayload(raw);
      setBacklogEditContext({
        workItemId,
        projectId: String(resolvedProjectId).trim(),
        normalizedWorkItem,
      });
      setOpenBacklogEditModal(true);
    } catch (err) {
      toast.error(
        getThunkRejectMessage(err, "Failed to load work item. Try again or contact support.")
      );
    } finally {
      setBacklogEditPrefillLoading(false);
    }
  };

  const requestOpenEditFromKanbanBacklogRow = async (issue) => {
    if (canEditWorkItem) {
      await openEditModalFromKanbanBacklogRow(issue);
      return;
    }
    if (canViewKanbanBoard && typeof onOpenWorkItemDetail === "function") {
      const wid = issue?.id ?? issue?.workItemId;
      if (wid == null || String(wid).trim() === "") {
        toast.error("Cannot open this row: missing work item id.");
        return;
      }
      onOpenWorkItemDetail(String(wid).trim());
      return;
    }
    setDeniedPermissionKey(
      canViewKanbanBoard ? PERMISSION_KEYS.WORK_ITEMS_EDIT : PERMISSION_KEYS.KANBAN_VIEW
    );
  };

  const getIssuesForColumn = (column) => {
    if (isGlobalBoard) {
      return filteredIssues.filter((i) => resolveGlobalKanbanColumnKey(i) === column.key);
    }
    return filteredIssues.filter(
      (i) => resolveProjectKanbanColumnKey(i, statusesResponse) === column.key
    );
  };

  if (!columns.length) {
    if (!isGlobalBoard && projectSlug && showKanbanLoadingOverlay) {
      return (
        <div className="relative isolate rounded-lg">
          <div
            className="sticky top-0 z-[1100] flex h-[calc(100dvh_-_11rem)] w-full items-center justify-center bg-white/[0.93]"
            aria-busy="true"
            aria-live="polite"
          >
            <Loader variant="inline" label="Loading" />
          </div>
        </div>
      );
    }
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <Typography variant="text1" className="text-text-secondary">
          No workflow statuses for this project. Configure statuses in project settings.
        </Typography>
      </div>
    );
  }

  return (
    <div className="relative isolate w-full max-w-full rounded-lg">
      {showKanbanLoadingOverlay && !statusPatchPending ? (
        <div
          className="sticky top-0 z-[1100] -mb-screen flex h-screen w-full shrink-0 items-center justify-center bg-white/[0.93] backdrop-blur-[1px]"
          aria-busy="true"
          aria-live="polite"
        >
          <Loader variant="inline" label="Loading work items" />
        </div>
      ) : null}
      {statusPatchPending ? (
        <div
          className="absolute inset-0 z-[1200] rounded-lg bg-white/70 backdrop-blur-[2px]"
          aria-busy="true"
          aria-live="polite"
        >
          <div className="sticky top-0 flex h-screen items-center justify-center">
            <div className="flex items-center gap-2.5 rounded-xl bg-white px-4 py-2.5 shadow-lg ring-1 ring-black/5">
              <Loader variant="inline" label="Updating status" size="sm" />
              <span className="text-sm font-medium text-gray-600">Updating…</span>
            </div>
          </div>
        </div>
      ) : null}
      <DndContext
        sensors={dragSensors}
        onDragStart={({ active }) => {
          if (statusPatchPending) return;
          setActiveId(active.id);
          const cardEl = document.getElementById(`kanban-card-${active.id}`);
          setDragPreviewWidth(cardEl?.offsetWidth ?? null);
        }}
        onDragEnd={({ active, over }) => {
          setActiveId(null);
          setDragPreviewWidth(null);
          if (statusPatchPending) return;
          if (!canEditWorkItem) {
            setDeniedPermissionKey(PERMISSION_KEYS.WORK_ITEMS_EDIT);
            return;
          }
          if (over && active.id !== over.id) {
            const targetColumn = columns.find((c) => c.key === String(over.id));
            if (!targetColumn || !onStatusChange) return;
            if (isGlobalBoard) {
              if (targetColumn.category) {
                onStatusChange(active.id, { category: targetColumn.category });
              }
              return;
            }
            const targetStatusId =
              targetColumn.statusId ??
              resolveProjectStatusIdForCategory(statusesResponse, targetColumn.category);
            if (targetStatusId != null) {
              onStatusChange(active.id, { statusId: targetStatusId });
            }
          }
        }}
        onDragCancel={() => {
          setActiveId(null);
          setDragPreviewWidth(null);
        }}
      >
        {/* ===================== FILTER BAR ===================== */}
        <div className="flex items-center gap-2 mb-4">
          {/* Search */}
          <div className={`relative min-w-0 ${isGlobalBoard ? "flex-1" : "w-[80%]"}`}>
            <RiSearchLine className="absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary text-lg" />
            <Input
              type="text"
              value={effectiveSearchTerm}
              onChange={(e) => setEffectiveSearchTerm(e.target.value)}
              placeholder="Search by keyword (title, issue key)..."
              noWrapper
              className="
            pl-10 pr-3 py-2
            text-sm
            rounded-sm
            bg-surface-primary
            border-border-primary
            focus:ring-primary
          "
            />
          </div>

          {isGlobalBoard ? (
            <>
              {typeof onShowOnHoldChange === "function" ? (
                <div className="flex items-center gap-2 shrink-0">
                  <Typography
                    variant="text2"
                    className="text-text-primary whitespace-nowrap m-0 p-0"
                  >
                    Show on hold
                  </Typography>
                  <ToggleSwitch
                    checked={!!showOnHold}
                    onChange={onShowOnHoldChange}
                    size="sm m-0 p-0"
                    aria-label="Show On Hold column on Kanban board"
                  />
                </div>
              ) : null}
              <GlobalKanbanQuickFilter
                users={usersListRaw}
                selectedIds={selectedAssignees}
                onSelectionChange={setSelectedAssignees}
              />
            </>
          ) : (
            /* Project Kanban: assignee avatar strip */
            <div className="relative w-[20%]" ref={assigneeFilterRef}>
              <div className="flex items-center min-h-[40px] px-2">
                {assigneeOptions.slice(0, 4).map((user) => {
                  const isActive = selectedAssignees.includes(user.id);

                  return (
                    <div
                      key={user.id}
                      title={user.name}
                      onClick={() =>
                        setSelectedAssignees((prev) => {
                          const p = prev ?? [];
                          return p.includes(user.id)
                            ? p.filter((a) => a !== user.id)
                            : [...p, user.id];
                        })
                      }
                      className={`w-9 h-9 border-radius-9999 flex items-center justify-center text-xs font-semibold text-white -ml-2 border-2 cursor-pointer ${
                        isActive ? "border-indigo-500 ring-2 ring-indigo-400" : "border-white"
                      }`}
                      style={{
                        backgroundColor: user.color,
                        borderRadius: "var(--radius-full)",
                      }}
                    >
                      {user.initials}
                    </div>
                  );
                })}

                {assigneeOptions.length > 4 && (
                  <div
                    className="w-9 h-9 -ml-2 bg-gray-200 text-xs flex items-center justify-center border-2 border-white cursor-pointer"
                    style={{ borderRadius: "var(--radius-full)" }}
                    onClick={() => setShowAssigneePopup((p) => !p)}
                  >
                    +{assigneeOptions.length - 4}
                  </div>
                )}
              </div>

              {showAssigneePopup && (
                <div className="absolute right-0 mt-2 w-full min-w-[220px] bg-white border rounded-lg shadow-lg z-50">
                  <div className="flex items-center justify-between gap-2 px-4 py-2 border-b border-gray-100">
                    <span className="text-sm font-semibold text-gray-500">Assignees</span>
                    <button
                      type="button"
                      className="text-xs font-medium text-indigo-600 hover:text-indigo-800 disabled:opacity-40 disabled:cursor-not-allowed shrink-0"
                      disabled={selectedAssignees.length === 0}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedAssignees([]);
                      }}
                    >
                      Clear all
                    </button>
                  </div>
                  {assigneeOptions.map((user) => {
                    const isSelected = selectedAssignees.includes(user.id);
                    return (
                      <div
                        key={user.id}
                        onClick={() =>
                          setSelectedAssignees((prev) => {
                            const p = prev ?? [];
                            return p.includes(user.id)
                              ? p.filter((a) => a !== user.id)
                              : [...p, user.id];
                          })
                        }
                        className={`flex items-center gap-3 px-4 py-2 cursor-pointer hover:bg-gray-100 ${
                          isSelected ? "bg-gray-100" : ""
                        }`}
                      >
                        <div
                          className="w-8 h-8 flex items-center justify-center text-xs font-semibold text-white"
                          style={{
                            backgroundColor: user.color,
                            borderRadius: "var(--radius-full)",
                          }}
                        >
                          {user.initials}
                        </div>
                        <Typography variant="span2">{user.name}</Typography>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        {/* ===================== BACKLOG ===================== */}
        <ProjectBacklog
          projectSlug={projectSlug}
          onRequestEditWorkItem={requestOpenEditFromKanbanBacklogRow}
        />

        {canCreateWorkItem ? (
          <div className="flex items-center justify-end gap-2">
            <AddButton
              className="ml-auto mt-3"
              onClick={() => setOpenCreateModal(true)}
              label="Create new work item"
            />
          </div>
        ) : null}

        {/* ===================== BOARD ===================== */}
        {/*
          Columns: min 220px at 100% zoom, grow equally (1fr) to fill the board width.
          Horizontal scroll only when columns cannot fit at minimum width.
        */}
        <div
          className="w-full overflow-x-auto scrollbar-hide"
          style={{ overflowY: "visible", ...boardStyle }}
        >
          <div
            className="grid w-full min-w-full min-h-[calc(100dvh-12rem)]"
            style={{
              gap: "var(--kanban-column-gap)",
              gridTemplateColumns,
            }}
          >
            {columns.map((column) => {
              const colState = columnData ? (columnData[column.key] ?? null) : null;
              // Controlled mode: `search` is sent to the API, so colState.items is already
              // scoped — no client re-filter needed. Uncontrolled fallback filters locally.
              const columnIssues = colState
                ? isSearchControlled
                  ? (colState.items ?? [])
                  : effectiveSearchTerm
                    ? (colState.items ?? []).filter((i) =>
                        issueMatchesKanbanSearch(i, effectiveSearchTerm)
                      )
                    : (colState.items ?? [])
                : getIssuesForColumn(column);

              return (
                <div className="flex min-h-[calc(100dvh-12rem)] w-full min-w-0 flex-col">
                  <Column
                    column={column}
                    issues={columnIssues}
                    count={
                      colState
                        ? isSearchControlled
                          ? (colState.total ?? colState.items?.length ?? 0)
                          : effectiveSearchTerm
                            ? columnIssues.length
                            : (colState.total ?? colState.items?.length ?? 0)
                        : columnIssues.length
                    }
                    colState={colState}
                    onLoadMore={onLoadMore}
                    isDragging={activeId !== null}
                    projectSlug={projectSlug}
                    createColumnKey={createColumnKey}
                    setCreateColumnKey={setCreateColumnKey}
                    assigneeOptions={assigneeOptions}
                    workTypeOptions={workTypeOptions}
                    priorityOptions={priorityOptions}
                    canCreateWorkItem={canCreateWorkItem}
                    canEditWorkItem={canEditWorkItem}
                    isGlobalBoard={isGlobalBoard}
                    onDeniedCreate={() => setDeniedPermissionKey(PERMISSION_KEYS.WORK_ITEMS_CREATE)}
                    onOpenWorkItemDetail={onOpenWorkItemDetail}
                    onRefetchProjectKanbanIssues={onRefetchProjectKanbanIssues}
                    showProjectName={isGlobalBoard}
                  />
                </div>
              );
            })}
          </div>
        </div>

        {/* ===================== DRAG OVERLAY ===================== */}
        <DragOverlay>
          {activeIssue ? (
            <div
              className="rotate-3 opacity-90 w-full min-w-0"
              style={dragPreviewWidth != null ? { width: dragPreviewWidth } : undefined}
            >
              <IssueCard
                issue={activeIssue}
                projectSlug={projectSlug}
                dragPreview
                showProjectName={isGlobalBoard}
              />
            </div>
          ) : null}
        </DragOverlay>

        {/* ===================== CREATE TASK MODAL (same as Work Items tab) ===================== */}
        <CreateModal
          open={openCreateModal}
          onClose={() => {
            setOpenCreateModal(false);
          }}
          submitDisabled={createSubmitting}
        >
          <CreateTaskForm
            projectsList={projectsList}
            clientsList={clientsList}
            currentProjectId={projectSlug}
            onSuccess={() => {
              setOpenCreateModal(false);
              if (onRefetchProjectKanbanIssues) {
                // Handles both project board and global board in lazy-loading mode.
                onRefetchProjectKanbanIssues();
              } else if (projectSlug) {
                dispatch(fetchWorkItemsByProjectId({ projectId: projectSlug }));
              } else {
                dispatch(fetchAllWorkItemsForGlobalKanban({ page: 1, limit: 100 }));
              }
            }}
            onSubmittingChange={setCreateSubmitting}
          />
        </CreateModal>

        <BacklogEditModal
          open={openBacklogEditModal}
          onClose={() => {
            setOpenBacklogEditModal(false);
            setBacklogEditContext(null);
          }}
          submitDisabled={backlogEditPrefillLoading || backlogEditSubmitting}
        >
          <BacklogTaskForm
            projectsList={projectsList}
            clientsList={clientsList}
            currentProjectId={projectSlug ?? backlogEditContext?.projectId ?? ""}
            editContext={backlogEditContext}
            onSubmittingChange={setBacklogEditSubmitting}
            onSuccess={() => {
              setOpenBacklogEditModal(false);
              setBacklogEditContext(null);
              const backlogPageSize = 10;
              const backlogParams = projectSlug
                ? { projectId: projectSlug, page: 1, limit: backlogPageSize }
                : { page: 1, limit: backlogPageSize };
              dispatch(fetchBacklogData(backlogParams));
              if (onRefetchProjectKanbanIssues) {
                onRefetchProjectKanbanIssues();
              } else if (projectSlug) {
                dispatch(fetchWorkItemsByProjectId({ projectId: projectSlug }));
              } else {
                dispatch(fetchAllWorkItemsForGlobalKanban({ page: 1, limit: 100 }));
              }
            }}
          />
        </BacklogEditModal>
      </DndContext>
      <PermissionDeniedModal
        isOpen={deniedPermissionKey != null}
        onClose={() => setDeniedPermissionKey(null)}
        permissionKey={deniedPermissionKey}
      />
    </div>
  );
};

/* ===================== COLUMN ===================== */
const Column = ({
  column,
  issues,
  projectSlug,
  createColumnKey,
  setCreateColumnKey,
  count,
  /** Per-column lazy-loading state from kanbanColumnsSlice. null when using flat-list mode. */
  colState,
  /** Called with columnKey when user clicks Load more / Load older. */
  onLoadMore,
  /** True while any card is being dragged — suppresses load-more footer to avoid visual noise. */
  isDragging = false,
  assigneeOptions,
  workTypeOptions,
  priorityOptions,
  canCreateWorkItem = true,
  canEditWorkItem = true,
  isGlobalBoard = false,
  onDeniedCreate,
  onOpenWorkItemDetail,
  onRefetchProjectKanbanIssues,
  /** Global board: show project name on each card (Jira-style cross-project context). */
  showProjectName = false,
}) => {
  const dispatch = useDispatch();
  const { setNodeRef, isOver } = useDroppable({ id: column.key });
  const [title, setTitle] = useState("");

  const [issueType, setIssueType] = useState("Task");
  const [startDate, setStartDate] = useState("");
  const [assigneeId, setAssigneeId] = useState("");
  const [inlineSubmitting, setInlineSubmitting] = useState(false);
  const [showTypeDropdown, setShowTypeDropdown] = useState(false);
  const [showAssigneePicker, setShowAssigneePicker] = useState(false);
  const [assigneePickerSearch, setAssigneePickerSearch] = useState("");

  const typeDropdownRef = useRef(null);
  const assigneePickerRef = useRef(null);

  const selectedIssueType = useMemo(() => {
    return KANBAN_INLINE_ISSUE_TYPES.find(
      (t) => String(t.key).toLowerCase() === String(issueType).toLowerCase()
    );
  }, [issueType]);

  const selectedAssignee = useMemo(() => {
    if (!assigneeId) return null;
    return assigneeOptions.find((u) => String(u.userId ?? "") === String(assigneeId)) ?? null;
  }, [assigneeOptions, assigneeId]);

  const handleCreate = async () => {
    if (!canCreateWorkItem) {
      onDeniedCreate?.();
      return;
    }
    const summary = String(title ?? "").trim();
    if (!summary) return;
    if (!projectSlug) return;
    const typeId = resolveTypeIdForLabel(workTypeOptions, issueType);
    if (!typeId) return;
    const mediumPriorityId = resolvePriorityIdForLabel(priorityOptions, "Medium");

    const isBugOrTask =
      String(issueType).toLowerCase() === "bug" || String(issueType).toLowerCase() === "task";

    setInlineSubmitting(true);
    try {
      await dispatch(
        createWorkItem({
          projectId: String(projectSlug),
          body: {
            typeId,
            parentId: isBugOrTask ? null : null,
            title: summary,
            description: null,
            priorityId: mediumPriorityId ?? null,
            assigneeId: assigneeId ? String(assigneeId) : null,
            originalEstimate: null,
            storyPoints: 0,
            startDate: startDate?.trim() || null,
            dueDate: null,
            statusId: column.statusId ?? null,
          },
        })
      ).unwrap();

      setTitle("");
      setIssueType("Task");
      setStartDate("");
      setAssigneeId("");
      setCreateColumnKey(null);
      if (onRefetchProjectKanbanIssues) {
        onRefetchProjectKanbanIssues();
      } else {
        dispatch(fetchWorkItemsByProjectId({ projectId: projectSlug }));
      }
    } finally {
      setInlineSubmitting(false);
    }
  };

  const assigneeDropdownOptions = useMemo(() => {
    return (assigneeOptions || [])
      .map((u) => ({
        value: u.userId ?? "",
        label: u.name ?? "N/A",
        avatar: u.initials ?? "",
        color: u.color ?? "#4B5563",
        showAvatar: true,
      }))
      .filter((o) => o.value);
  }, [assigneeOptions]);

  const filteredAssigneeDropdownOptions = useMemo(() => {
    const q = assigneePickerSearch.trim().toLowerCase();
    if (!q) return assigneeDropdownOptions;
    return assigneeDropdownOptions.filter((opt) => {
      const label = String(opt.label ?? "").toLowerCase();
      const initials = String(opt.avatar ?? "").toLowerCase();
      return label.includes(q) || initials.includes(q);
    });
  }, [assigneeDropdownOptions, assigneePickerSearch]);

  useEffect(() => {
    if (!showAssigneePicker) setAssigneePickerSearch("");
  }, [showAssigneePicker]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (typeDropdownRef.current && !typeDropdownRef.current.contains(e.target)) {
        setShowTypeDropdown(false);
      }
      if (assigneePickerRef.current && !assigneePickerRef.current.contains(e.target)) {
        setShowAssigneePicker(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div ref={setNodeRef} className={isOver ? "ring-2 ring-blue-500 ring-offset-2 rounded-lg" : ""}>
      <BoardColumn title={column.label} colorClass={column.color} count={count}>
        {issues.map((issue) => (
          <DraggableIssue
            key={issue.id}
            issue={issue}
            projectSlug={projectSlug}
            canDrag={canEditWorkItem}
            onOpenWorkItemDetail={onOpenWorkItemDetail}
            showProjectName={showProjectName}
          />
        ))}

        {/* INITIAL LOADING skeleton for lazy-loading mode */}
        {colState?.initialLoading && (
          <div className="flex justify-center py-4">
            <Loader variant="inline" label="Loading" />
          </div>
        )}

        {/* CREATE TASK – default / TODO column from project workflow */}
        {column.isCreateColumn &&
          (createColumnKey !== column.key ? (
            <>
              <Button
                type="button"
                variant="icon"
                size="md"
                onClick={() => {
                  if (!canCreateWorkItem) {
                    onDeniedCreate?.();
                    return;
                  }
                  setCreateColumnKey(column.key);
                }}
                className="flex items-center gap-2
                !m-0 !p-0
                text-gray-600
                hover:text-indigo-600
                border-0
              "
              >
                <Typography variant="span1" className="text-lg">
                  +
                </Typography>{" "}
                Create
              </Button>
            </>
          ) : (
            <div className="mt-2 w-full min-w-0 max-w-full bg-white border border-gray-200 rounded-md p-2 shadow-sm mb-3">
              {/* TITLE */}

              <Input
                autoFocus
                noWrapper
                type="text"
                placeholder="What needs to be done?"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleCreate();
                  if (e.key === "Escape") setCreateColumnKey(null);
                }}
                className="
                w-full min-w-0
                bg-transparent
                text-xs
                text-gray-800
                placeholder-gray-400
                border-0
                rounded-none
                focus:ring-0
                outline-none
              "
              />

              {/* ACTION ROW */}
              <div className="mt-2 flex min-w-0 w-full items-center justify-between gap-1.5">
                {/* LEFT */}
                <div className="flex min-w-0 flex-1 items-center gap-1.5 text-gray-600">
                  {/* TYPE (compact trigger) */}
                  <div className="relative shrink-0" ref={typeDropdownRef}>
                    <div
                      className="flex cursor-pointer items-center gap-1 rounded-md px-1.5 py-0.5 hover:bg-gray-50 hover:text-indigo-600"
                      onClick={() => setShowTypeDropdown((p) => !p)}
                    >
                      {selectedIssueType?.icon ? (
                        <selectedIssueType.icon
                          size={14}
                          className={selectedIssueType?.color ?? ""}
                        />
                      ) : null}
                      <Typography
                        variant="span2"
                        className={`text-xs font-medium ${selectedIssueType?.color ?? ""}`}
                      >
                        {issueType}
                      </Typography>
                      <RiArrowDownSLine className="shrink-0 text-base" />
                    </div>

                    {showTypeDropdown ? (
                      <div className="absolute left-0 top-full mt-1 bg-white border rounded-md shadow-lg z-50 w-40">
                        {KANBAN_INLINE_ISSUE_TYPES.map((type) => {
                          const Icon = type.icon;
                          return (
                            <div
                              key={type.key}
                              onClick={() => {
                                setIssueType(type.key);
                                setShowTypeDropdown(false);
                              }}
                              className={`flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-gray-100 ${type.color}`}
                            >
                              <Icon size={16} />
                              <Typography variant="span2">{type.label}</Typography>
                            </div>
                          );
                        })}
                      </div>
                    ) : null}
                  </div>

                  {/* Start date (inline create — sent as startDate on create work item) */}
                  <div className="min-w-0 max-w-[7.25rem] flex-1 sm:max-w-[9rem]">
                    <SingleDatePicker
                      value={startDate ? { date: startDate } : null}
                      onChange={(val) => setStartDate(val?.date ?? "")}
                      placeholder={DISPLAY_DATE_FORMAT_PICKER_MOMENT}
                      showIcon={true}
                      className="!h-8 !py-1 !text-[11px] !leading-tight !pl-8 !pr-1.5"
                    />
                  </div>

                  {/* ASSIGNEE (compact trigger) */}
                  <div className="relative min-w-0 max-w-[38%] shrink" ref={assigneePickerRef}>
                    <div
                      className="flex min-w-0 cursor-pointer items-center gap-1 rounded-md px-1 py-0.5 hover:bg-gray-50 hover:text-indigo-600"
                      onClick={() => setShowAssigneePicker((p) => !p)}
                      title={selectedAssignee?.name || "Assignee"}
                    >
                      {selectedAssignee ? (
                        <>
                          <Typography
                            variant="span2"
                            className="min-w-0 truncate text-[11px] text-gray-700"
                          >
                            {selectedAssignee.name}
                          </Typography>
                          <RiArrowDownSLine className="shrink-0 text-gray-500" />
                        </>
                      ) : (
                        <>
                          <RiUser3Line size={15} className="shrink-0" />
                          <Typography
                            variant="span2"
                            className="truncate text-[11px] text-gray-700"
                          >
                            Assignee
                          </Typography>
                          <RiArrowDownSLine className="shrink-0" />
                        </>
                      )}
                    </div>

                    {showAssigneePicker ? (
                      <div className="absolute left-0 top-full z-50 mt-1 flex min-w-[240px] max-w-[320px] flex-col rounded-md border bg-white shadow-lg">
                        <div className="shrink-0 border-b border-gray-100 p-2">
                          <div className="relative">
                            <RiSearchLine
                              className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400"
                              aria-hidden
                            />
                            <Input
                              autoFocus
                              noWrapper
                              type="text"
                              value={assigneePickerSearch}
                              onChange={(e) => setAssigneePickerSearch(e.target.value)}
                              onKeyDown={(e) => {
                                e.stopPropagation();
                                if (e.key === "Escape") setShowAssigneePicker(false);
                              }}
                              placeholder="Search assignees..."
                              aria-label="Search assignees"
                              className="w-full rounded-md border border-gray-200 bg-gray-50 py-1.5 pl-7 pr-2 text-xs text-gray-800 placeholder:text-gray-400 focus:border-indigo-300 focus:bg-white focus:outline-none focus:ring-1 focus:ring-indigo-400"
                            />
                          </div>
                        </div>
                        <div className="max-h-[220px] min-h-0 overflow-y-auto">
                          <div
                            className="cursor-pointer px-3 py-2 text-sm hover:bg-gray-100"
                            onClick={() => {
                              setAssigneeId("");
                              setShowAssigneePicker(false);
                            }}
                          >
                            Unassigned
                          </div>

                          {filteredAssigneeDropdownOptions.length === 0 ? (
                            <div className="px-3 py-3 text-center text-xs text-gray-500">
                              No assignees match your search.
                            </div>
                          ) : (
                            filteredAssigneeDropdownOptions.map((opt) => (
                              <div
                                key={opt.value}
                                className="flex cursor-pointer items-center gap-3 px-3 py-2 text-sm hover:bg-gray-100"
                                onClick={() => {
                                  setAssigneeId(opt.value);
                                  setShowAssigneePicker(false);
                                }}
                              >
                                <div
                                  className="flex h-7 min-h-[28px] w-7 min-w-[28px] items-center justify-center text-xs font-semibold text-white"
                                  style={{
                                    borderRadius: "var(--radius-full)",
                                    backgroundColor: opt.color,
                                  }}
                                >
                                  {opt.avatar}
                                </div>
                                <Typography variant="span2">{opt.label}</Typography>
                              </div>
                            ))
                          )}
                        </div>
                      </div>
                    ) : null}
                  </div>
                </div>

                {/* CREATE ICON */}
                <RiCornerDownLeftLine
                  size={16}
                  className={`shrink-0 cursor-pointer text-gray-400 hover:text-indigo-600 ${inlineSubmitting ? "pointer-events-none opacity-50" : ""}`}
                  onClick={inlineSubmitting ? undefined : handleCreate}
                />
              </div>
            </div>
          ))}

        {/* LOAD MORE footer — only shown in lazy-loading mode, hidden while dragging */}
        {!isDragging && colState?.hasMore && !colState?.loadingMore && (
          <button
            type="button"
            className="group mt-3 w-full flex items-center justify-center gap-1 rounded-lg border border-dashed border-indigo-200 bg-white py-2 text-xs font-semibold text-indigo-500 hover:border-indigo-400 hover:bg-indigo-50 hover:text-indigo-700 active:scale-[0.98] transition-all duration-150"
            onClick={() => onLoadMore?.(column.key)}
          >
            <RiArrowDownSLine
              size={14}
              className="transition-transform duration-200 group-hover:translate-y-0.5"
            />
            {column.category === "CLOSED" || column.category === "DONE"
              ? "Load older"
              : "Load more"}
          </button>
        )}
        {!isDragging && colState?.loadingMore && (
          <div className="mt-3 flex items-center justify-center gap-2 rounded-lg border border-dashed border-indigo-100 bg-indigo-50/50 py-2">
            <Loader variant="inline" label="Loading" size="sm" />
            <span className="text-xs font-medium text-indigo-400">Loading…</span>
          </div>
        )}
        {colState?.error && !colState?.loadingMore && (
          <div className="mt-2 rounded-md bg-red-50 px-3 py-2 text-xs text-red-600">
            {colState.error}{" "}
            <button
              type="button"
              className="underline hover:text-red-800"
              onClick={() => onLoadMore?.(column.key)}
            >
              Retry
            </button>
          </div>
        )}
      </BoardColumn>
    </div>
  );
};

/* ===================== DRAGGABLE ISSUE ===================== */
const DraggableIssue = ({
  issue,
  projectSlug,
  canDrag = true,
  onOpenWorkItemDetail,
  showProjectName = false,
}) => {
  const { attributes, listeners, setNodeRef, transform } = useDraggable({
    id: issue.id,
    disabled: !canDrag,
  });

  return (
    <div
      id={`kanban-card-${issue.id}`}
      ref={setNodeRef}
      className="w-full min-w-0 max-w-full"
      style={
        transform ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)` } : undefined
      }
      {...(canDrag ? listeners : {})}
      {...(canDrag ? attributes : {})}
    >
      <IssueCard
        issue={issue}
        projectSlug={projectSlug}
        dragDisabled={!canDrag}
        onOpenWorkItemDetail={onOpenWorkItemDetail}
        showProjectName={showProjectName}
      />
    </div>
  );
};

export default KanbanBoard;
